import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { identityApi } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthProvider';

/**
 * The signed-in user's interface preferences, saved to their account.
 *
 * Preferences follow the person rather than the browser, so a new laptop, a cleared cache or a
 * phone opens the screens the way they left them. The server stores the document without reading
 * it; what goes in it is this interface's business.
 *
 * A copy is kept in localStorage, keyed by user, purely so a screen renders with the right density
 * and panels on its first paint instead of flashing the defaults while the request is in flight.
 * The server's copy wins as soon as it arrives. Keying the cache by user matters on a shared
 * machine: without it, the next person to sign in would briefly inherit the last one's layout.
 *
 * Writes are debounced. Toggling five columns in a row is one save, not five.
 */

export type PreferencesDocument = Record<string, unknown>;

const SAVE_DELAY_MS = 800;

function cacheKey(userId: string) {
  return `dam.preferences.${userId}`;
}

function readCache(userId: string): PreferencesDocument {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as PreferencesDocument) : {};
  } catch {
    return {};
  }
}

function writeCache(userId: string, document: PreferencesDocument) {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(document));
  } catch {
    // Without a cache the first paint shows defaults; the server copy still applies.
  }
}

interface UserPreferencesValue {
  document: PreferencesDocument;
  /** Replaces one top-level section, leaving the others untouched. */
  setSection: (section: string, value: unknown) => void;
}

const UserPreferencesContext = createContext<UserPreferencesValue | null>(null);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.userId ?? 'anonymous';
  const [document, setDocument] = useState<PreferencesDocument>(() => readCache(userId));
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<PreferencesDocument | null>(null);
  // Set once the user touches a setting, so a late server response cannot overwrite a change they
  // made while it was loading.
  const editedLocally = useRef(false);

  useEffect(() => {
    editedLocally.current = false;
    setDocument(readCache(userId));
    if (!user) return;

    let cancelled = false;
    identityApi
      .preferences()
      .then((remote) => {
        if (cancelled || editedLocally.current) return;
        setDocument(remote);
        writeCache(userId, remote);
      })
      .catch(() => {
        // Offline or the endpoint failed: keep working from the cached copy.
      });
    return () => {
      cancelled = true;
    };
  }, [user, userId]);

  const flush = useCallback(() => {
    saveTimer.current = null;
    const document = pending.current;
    pending.current = null;
    if (!document) return;
    identityApi.savePreferences(document).catch(() => {
      // A failed save leaves the local copy in place; the next change sends the whole document.
    });
  }, []);

  const setSection = useCallback(
    (section: string, value: unknown) => {
      editedLocally.current = true;
      setDocument((current) => {
        const next = { ...current, [section]: value };
        writeCache(userId, next);

        pending.current = next;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(flush, SAVE_DELAY_MS);

        return next;
      });
    },
    [userId, flush],
  );

  // A change made in the last moment before the provider goes away - signing out, closing the
  // shell - is sent immediately rather than dropped with the timer.
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      flush();
    },
    [flush],
  );

  const value = useMemo(() => ({ document, setSection }), [document, setSection]);

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

/** Returns an empty document outside the provider, so components render in tests without it. */
export function useUserPreferences(): UserPreferencesValue {
  return useContext(UserPreferencesContext) ?? { document: {}, setSection: () => {} };
}
